// ==================== SETTINGS PAGE ====================
// src/pages/Settings.tsx
import React, { useState, useEffect } from "react";
import { Save } from "lucide-react";
import { authClient } from "../auth/auth-client";

export default function Settings() {
  const { data: session } = authClient.useSession();
  const [displayName, setDisplayName] = useState("");
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMessage, setProfileMessage] = useState<string | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [passwordMessage, setPasswordMessage] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSending, setPasswordSending] = useState(false);

  const [config, setConfig] = useState({
    sections: ["Went well", "Could be improved", "Ideas", "Kudos"],
    allowAnonymous: true,
    maxGroupsPerRetro: 8,
    defaultDueDays: 14,
    managerSlackTag: "",
  });

  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setDisplayName(session?.user?.name || "");
  }, [session?.user?.name]);

  async function handleProfileSave() {
    const nextName = displayName.trim();
    if (!nextName) {
      setProfileError("Name cannot be empty.");
      setProfileMessage(null);
      return;
    }

    setProfileSaving(true);
    setProfileError(null);
    setProfileMessage(null);

    try {
      const result = await authClient.updateUser({
        name: nextName,
      });

      if (result?.error) {
        throw result.error;
      }

      setProfileMessage("Profile name updated.");
    } catch (error: any) {
      setProfileError(error?.message || "Could not update your name.");
    } finally {
      setProfileSaving(false);
    }
  }

  async function handleSendPasswordReset() {
    if (!session?.user?.email) {
      setPasswordError("No account email found in your session.");
      setPasswordMessage(null);
      return;
    }

    const appURL = (process.env.REACT_APP_FRONTEND_URL || window.location.origin).replace(
      /\/$/,
      "",
    );
    const resetRedirectURL = `${appURL}/login`;

    setPasswordSending(true);
    setPasswordError(null);
    setPasswordMessage(null);

    try {
      const result = await authClient.requestPasswordReset({
        email: session.user.email,
        redirectTo: resetRedirectURL,
      });

      if (result?.error) {
        throw result.error;
      }

      setPasswordMessage("Password reset link sent. Check your inbox.");
    } catch (error: any) {
      setPasswordError(
        error?.message || "Could not send password reset email.",
      );
    } finally {
      setPasswordSending(false);
    }
  }

  function handleSave() {
    // API call to save settings
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  }

  return (
    <div className="p-8 max-w-4xl">
      <h1 className="text-3xl font-bold mb-8">Settings</h1>

      <div className="bg-white rounded-lg shadow mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold">Profile</h2>
        </div>
        <div className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email
            </label>
            <input
              type="email"
              value={session?.user?.email || ""}
              readOnly
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
            />
            <p className="text-xs text-gray-500 mt-1">
              Email is managed by your authentication provider.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Name
            </label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Your display name"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div className="pt-2">
            <button
              onClick={handleProfileSave}
              disabled={profileSaving}
              className="px-5 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-60"
            >
              {profileSaving ? "Saving..." : "Save Profile"}
            </button>
            {profileMessage && (
              <div className="mt-2 text-sm text-green-600">{profileMessage}</div>
            )}
            {profileError && (
              <div className="mt-2 text-sm text-red-600">{profileError}</div>
            )}
          </div>

          <div className="pt-4 border-t border-gray-200 space-y-3">
            <h3 className="text-sm font-semibold text-gray-800">Password</h3>
            <p className="text-sm text-gray-600">
              To change your password, request a reset link. We&apos;ll email you a
              secure link that lets you set a new password.
            </p>
            <ol className="list-decimal pl-5 text-sm text-gray-600 space-y-1">
              <li>Click "Send Password Reset Link" below.</li>
              <li>Open the email sent to your account address.</li>
              <li>Use the link to choose and confirm a new password.</li>
            </ol>
            <button
              onClick={handleSendPasswordReset}
              disabled={passwordSending || !session?.user?.email}
              className="px-5 py-2 border border-indigo-600 text-indigo-700 rounded-lg hover:bg-indigo-50 transition-colors disabled:opacity-60"
            >
              {passwordSending ? "Sending..." : "Send Password Reset Link"}
            </button>
            {passwordMessage && (
              <div className="text-sm text-green-600">{passwordMessage}</div>
            )}
            {passwordError && (
              <div className="text-sm text-red-600">{passwordError}</div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold">Retro Configuration</h2>
        </div>
        <div className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Retro Sections
            </label>
            <div className="space-y-2">
              {config.sections.map((section, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={section}
                    onChange={(e) => {
                      const newSections = [...config.sections];
                      newSections[i] = e.target.value;
                      setConfig({ ...config, sections: newSections });
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <button
                    onClick={() => {
                      const newSections = config.sections.filter(
                        (_, idx) => idx !== i,
                      );
                      setConfig({ ...config, sections: newSections });
                    }}
                    className="px-3 py-2 text-red-600 hover:bg-red-50 rounded"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
            <button
              onClick={() =>
                setConfig({
                  ...config,
                  sections: [...config.sections, "New Section"],
                })
              }
              className="mt-2 text-sm text-indigo-600 hover:text-indigo-800"
            >
              + Add Section
            </button>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={config.allowAnonymous}
              onChange={(e) =>
                setConfig({ ...config, allowAnonymous: e.target.checked })
              }
              className="rounded"
            />
            <label className="text-sm font-medium text-gray-700">
              Allow anonymous submissions
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Manager Slack tag (user ID or @handle)
            </label>
            <input
              type="text"
              value={config.managerSlackTag}
              onChange={(e) =>
                setConfig({ ...config, managerSlackTag: e.target.value })
              }
              placeholder="e.g. U12345678 or @manager"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Only this user can generate monthly reports in Slack.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Maximum groups per retro
            </label>
            <input
              type="number"
              value={config.maxGroupsPerRetro}
              onChange={(e) =>
                setConfig({
                  ...config,
                  maxGroupsPerRetro: parseInt(e.target.value),
                })
              }
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Default action item due date (days from creation)
            </label>
            <input
              type="number"
              value={config.defaultDueDays}
              onChange={(e) =>
                setConfig({
                  ...config,
                  defaultDueDays: parseInt(e.target.value),
                })
              }
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div className="pt-4 border-t">
            <button
              onClick={handleSave}
              className="flex items-center gap-2 px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              <Save size={20} />
              Save Settings
            </button>
            {saved && (
              <div className="mt-2 text-sm text-green-600">
                Settings saved successfully!
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-6 bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold">Slack Integration</h2>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Bot Token
            </label>
            <input
              type="password"
              value="xoxb-****-****-****"
              readOnly
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Signing Secret
            </label>
            <input
              type="password"
              value="****-****-****"
              readOnly
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
            />
          </div>
          <div className="text-sm text-gray-600">
            <p>
              Interaction URL:{" "}
              <code className="bg-gray-100 px-2 py-1 rounded">
                https://your-domain.com/slack/interactions
              </code>
            </p>
            <p className="mt-1">
              Command URL:{" "}
              <code className="bg-gray-100 px-2 py-1 rounded">
                https://your-domain.com/slack/commands
              </code>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
